import { create } from "zustand";
import { Room } from "colyseus.js";
import * as THREE from "three";
import {
  getColyseusClient,
  getCurrentRoom,
  createRoom,
  joinRoom,
  leaveRoom as leaveColyseusRoom,
  reconnectRoom,
  clearReconnectToken,
  sendMovePosition,
  sendShoot,
  sendShootWithPosition,
  sendShootVolley,
  sendChargeUpdate,
  sendUseItem,
  sendUpdateSkin,
  sendUpdateEffect,
  sendUpdateTank,
  sendStartGame,
  sendTogglePause,
  sendChangeColor,
  sendAbandon,
  sendToggleReady,
  sendUpdateRules,
  sendRestartGame,
  PlayerState,
  BulletState,
  ItemState,
  RocketanksStateType,
  GameRulesOptions,
  GameRulesState,
} from "./client";
import { useRocketanks, type ObstacleData } from "../stores/useRocketanks";
import { useProgress } from "../stores/useProgress";
import { snapshotInterpolation } from "./SnapshotInterpolation";
import type { CosmeticEffectType } from "@/lib/effects/effectProfiles";

// Reconexão config
const RECONNECT_KEY = "rocketanks_reconnect";
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY_MS = 2000;

interface ReconnectData {
  roomCode: string;
  playerName: string;
  timestamp: number;
}

export interface ColyseusPlayer {
  id: string;
  sessionId: string;
  name: string;
  position: { x: number; y: number; z: number };
  rotation: number;
  turretRotation: number;
  health: number;
  maxHealth: number;
  color: string;
  isAlive: boolean;
  isReady: boolean;
  kills: number;
  deaths: number;
  isBot: boolean;
  activeEffect: string;
  effectExpiresAt: number;
  heldItem: string;
  shieldHits: number;
  frozenUntil: number;
  decoyX: number;
  decoyZ: number;
  decoyExpiresAt: number;
  lives: number; // [FIX-17] Vidas restantes
  // [SYNC] Estado de carregamento visível para outros jogadores
  isCharging: boolean;
  chargeLevel: number;
  // [SYNC] Skin cosmética visível para outros jogadores
  skinId: string;
  // [SYNC] Efeito cosmético do tanque (aura/rastro) visível para outros jogadores
  effectId: string;
  // [SYNC] Tanque selecionado — permite renderizar o body kit da linhagem,
  // número de canhões e escala do tanque remoto a partir do catálogo local
  tankId: string;
  // [SPECIALS] Poder de linhagem resolvido pelo servidor (tankId → catálogo)
  special: string;
  specialPower: number;
  stealthed: boolean;
  // [BESTIÁRIO] Em chamas pelo sopro do Dragão até este timestamp (0 = não)
  burningUntil: number;
}

export interface ColyseusBullet {
  id: string;
  ownerId: string;
  position: { x: number; y: number; z: number };
  direction: { x: number; y: number; z: number };
  speed: number;
  damage: number;
  size: number;
  cosmeticEffectId?: string | null;
  cosmeticEffectType?: CosmeticEffectType | null;
}

export interface ColyseusItem {
  id: string;
  type: string;
  position: { x: number; y: number; z: number };
  createdAt: number;
  expiresAt: number;
}

/**
 * [BR] Parâmetros da zona do Battle Royale multiplayer. O servidor sincroniza
 * apenas os parâmetros do segmento de encolhimento (timestamps em serverTime);
 * o tamanho atual é interpolado localmente com getBRZoneSizeAt() usando o
 * relógio sincronizado — suave e sem custo de banda durante o encolhimento.
 */
export interface ColyseusBRZone {
  fromSize: number;
  toSize: number;
  shrinkStartAt: number;
  shrinkEndAt: number;
  nextShrinkAt: number;
  phase: number;
}

/** [BR] Largura atual da zona no instante serverNow (mesma fórmula do servidor). */
export function getBRZoneSizeAt(zone: ColyseusBRZone, serverNow: number): number {
  if (serverNow >= zone.shrinkEndAt) return zone.toSize;
  if (serverNow <= zone.shrinkStartAt) return zone.fromSize;
  const t = (serverNow - zone.shrinkStartAt) / (zone.shrinkEndAt - zone.shrinkStartAt);
  return zone.fromSize + (zone.toSize - zone.fromSize) * t;
}

/** [BR] Se a zona está encolhendo neste instante. */
export function isBRZoneShrinkingAt(zone: ColyseusBRZone, serverNow: number): boolean {
  return zone.toSize < zone.fromSize && serverNow >= zone.shrinkStartAt && serverNow < zone.shrinkEndAt;
}

export interface ColyseusState {
  // Conexão
  isConnected: boolean;
  room: Room<RocketanksStateType> | null;
  sessionId: string | null;
  
  // Estado do jogo
  roomCode: string | null;
  isHost: boolean;
  players: Map<string, ColyseusPlayer>;
  bullets: ColyseusBullet[];
  items: ColyseusItem[];
  gameStatus: "lobby" | "waiting" | "countdown" | "playing" | "paused" | "finished";
  countdownTime: number;
  gameSeed: number;
  terrain: string | null;
  
  // Winner
  winnerId: string | null;
  winnerName: string | null;
  endReason: string | null;
  
  // Rules
  rules: GameRulesState | null;
  gameTimeRemaining: number;

  // [BR] Zona do Battle Royale (null fora do modo BR)
  brZone: ColyseusBRZone | null;
  
  // Compatibilidade com interface useMultiplayer
  localPlayer: ColyseusPlayer | null;
  winner: { id: string; name: string } | null;
  
  // Erros e reconexão
  error: string | null;
  isReconnecting: boolean;
  reconnectAttempts: number;
  canReconnect: boolean;
  
  // Actions
  connect: () => void;
  disconnect: () => Promise<void>;
  createRoom: (playerName: string, terrain?: string) => Promise<boolean>;
  joinRoom: (code: string, playerName: string) => Promise<boolean>;
  leaveRoom: () => Promise<void>;
  startGame: () => void;
  restartGame: () => void;
  togglePause: (paused: boolean) => void;
  changeColor: (color: string) => void;
  abandon: () => void;
  toggleReady: () => void;
  updateRules: (rules: GameRulesOptions) => void;
  
  // Input (chamado pelo Tank)
  // Input (chamado pelo Tank)
  sendMove: (position: { x: number; y: number; z: number }, rotation: number, turretRotation?: number) => void;
  sendShoot: (bulletId: string, aimAngle: number) => void;
  sendShootFull: (
    bulletId: string,
    position: { x: number; y: number; z: number },
    direction: { x: number; y: number; z: number },
    metadata?: {
      speed?: number;
      damage?: number;
      size?: number;
      cosmeticEffectId?: string | null;
      cosmeticEffectType?: CosmeticEffectType | null;
    }
  ) => void;
  sendHit: (targetId: string, damage: number) => void;
  // [NETCODE] Disparo completo em uma mensagem: multi-tiro + carga
  sendShootVolley: (
    shots: { id: string; angle: number }[],
    chargeLevel: number,
    metadata?: {
      cosmeticEffectId?: string | null;
      cosmeticEffectType?: CosmeticEffectType | null;
    }
  ) => void;
  sendChargeUpdate: (isCharging: boolean, chargeLevel: number) => void;
  sendUseItem: () => void;
  sendUpdateSkin: (skinId: string) => void;
  
  // Reconexão
  tryReconnect: () => Promise<boolean>;
  cancelReconnect: () => void;
  checkReconnect: () => boolean;
  
  // Utils
  getLocalPlayer: () => ColyseusPlayer | null;
  clearError: () => void;
  reset: () => void;
}

/**
 * Estrutura de erro retornado pelo Colyseus
 * code: código numérico (4210-4217)
 * message: descrição do erro em inglês
 */
interface ColyseusError {
  code?: number;
  message?: string;
}

/**
 * Traduz erros do Colyseus para mensagens amigáveis em português
 * Código de erro 4212 = MATCHMAKE_INVALID_ROOM_ID (sala não encontrada)
 * Código de erro 4215 = AUTH_FAILED (autenticação falhou)
 * Código de erro 4213 = MATCHMAKE_UNHANDLED (erro interno)
 * Código de erro 4214 = MATCHMAKE_EXPIRED (sala expirou)
 */
function translateColyseusError(error: ColyseusError, operation: "create" | "join"): string {
  const code = error?.code;
  const msg = (error?.message || "").toLowerCase();

  // Timeout (adicionado pelo nosso wrapper withTimeout)
  if (msg.includes("timeout") || msg.includes("verifique")) {
    return error.message ?? "Timeout de conexão. Verifique sua internet.";
  }

  // Sala não encontrada ou código inválido
  if (code === 4212 || msg.includes("not found") || msg.includes("não encontrad")) {
    return "Sala não encontrada. Verifique o código e tente novamente.";
  }

  // Sala cheia
  if (msg.includes("full") || msg.includes("cheia") || msg.includes("already full")) {
    return "Sala cheia. Aguarde uma vaga ou crie outra sala.";
  }

  // Sala bloqueada (jogo em andamento)
  if (msg.includes("locked") || msg.includes("is locked")) {
    return "Não é possível entrar agora — o jogo já começou.";
  }

  // Autenticação falhou
  if (code === 4215 || msg.includes("auth")) {
    return "Falha na autenticação. Tente reconectar.";
  }

  // Sala expirou
  if (code === 4214 || msg.includes("expired") || msg.includes("expirou")) {
    return "A sala expirou. Peça ao host para criar uma nova sala.";
  }

  // Servidor não encontrou handler
  if (code === 4210 || msg.includes("no handler")) {
    return "Servidor de multiplayer indisponível. Tente mais tarde.";
  }

  // Erro de rede genérico
  if (msg.includes("network") || msg.includes("failed to fetch") || msg.includes("connection")) {
    return operation === "join"
      ? "Erro de conexão ao entrar na sala. Verifique sua internet."
      : "Erro de conexão ao criar sala. Verifique sua internet.";
  }

  // Fallback com mensagem original
  return error?.message || (operation === "join" ? "Sala não encontrada" : "Falha ao criar sala");
}

/**
 * Hook Zustand para Colyseus - alternativa ao useMultiplayer (Socket.io)
 */
export const useColyseus = create<ColyseusState>((set, get) => {
  // Flag para evitar re-inicialização do jogo a cada state change
  let gameInitialized = false;
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastInputSentAt = 0;
  let lastMoveSentAt = 0;
  let lastChargeSentAt = 0;

  const MIN_INPUT_INTERVAL_MS = 33;
  const MIN_MOVE_INTERVAL_MS = 33;
  const MIN_CHARGE_INTERVAL_MS = 80;
  
  // Helper para salvar dados de reconexão
  function saveReconnectData(roomCode: string, playerName: string) {
    const data: ReconnectData = {
      roomCode,
      playerName,
      timestamp: Date.now(),
    };
    try {
      sessionStorage.setItem(RECONNECT_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn("[useColyseus] Failed to save reconnect data:", e);
    }
  }
  
  // Helper para limpar dados de reconexão
  function clearReconnectData() {
    try {
      sessionStorage.removeItem(RECONNECT_KEY);
    } catch (e) {
      console.warn("[useColyseus] Failed to clear reconnect data:", e);
    }
  }
  
  // Helper para obter dados de reconexão
  function getReconnectData(): ReconnectData | null {
    try {
      const data = sessionStorage.getItem(RECONNECT_KEY);
      if (!data) return null;
      const parsed = JSON.parse(data) as ReconnectData;
      // Dados válidos por 5 minutos
      if (Date.now() - parsed.timestamp > 5 * 60 * 1000) {
        clearReconnectData();
        return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }
  
  /**
   * Configura listeners de estado da sala
   */
  function setupRoomListeners(room: Room<RocketanksStateType>, playerName?: string) {
    // Salvar dados para reconexão quando a sala é conectada
    if (playerName && room.id) {
      saveReconnectData(room.id, playerName);
    }

    // Ouvir mudanças de skin local para sincronizar com outros jogadores
    const skinChangedHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      sendUpdateSkin(detail.skinId || "");
    };
    window.addEventListener("skinSelected", skinChangedHandler);

    // [SPECIALS] Troca de tanque na garagem enquanto no lobby: o servidor
    // recalcula o poder de linhagem (só é aceito com a sala em "waiting").
    const tankChangedHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      sendUpdateTank(detail.tankId || "");
    };
    window.addEventListener("tankSelected", tankChangedHandler);

    // Troca de efeito cosmético (aura/rastro): sincronizar com outros jogadores
    const effectChangedHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      sendUpdateEffect(detail.effectId || "");
    };
    window.addEventListener("effectSelected", effectChangedHandler);

    // [NETCODE] Conversão de PlayerState → ColyseusPlayer.
    // As posições são lidas DIRETO do estado a cada patch (room.onStateChange),
    // sem depender dos callbacks onChange do MapSchema — que não disparam de
    // forma confiável para mutações profundas e deixavam jogadores "congelados".
    const toColyseusPlayer = (player: PlayerState): ColyseusPlayer => ({
      id: player.id,
      sessionId: player.sessionId,
      name: player.name,
      position: { x: player.x, y: player.y, z: player.z },
      rotation: player.rotation,
      turretRotation: player.turretRotation,
      health: player.health,
      maxHealth: player.maxHealth,
      color: player.color,
      isAlive: player.isAlive,
      isReady: player.isReady,
      kills: player.kills,
      deaths: player.deaths,
      isBot: (player as any).isBot ?? false,
      activeEffect: (player as any).activeEffect ?? "",
      effectExpiresAt: (player as any).effectExpiresAt ?? 0,
      heldItem: (player as any).heldItem ?? "",
      shieldHits: (player as any).shieldHits ?? 0,
      frozenUntil: (player as any).frozenUntil ?? 0,
      decoyX: (player as any).decoyX ?? 0,
      decoyZ: (player as any).decoyZ ?? 0,
      decoyExpiresAt: (player as any).decoyExpiresAt ?? 0,
      lives: (player as any).lives ?? 1,
      isCharging: (player as any).isCharging ?? false,
      chargeLevel: (player as any).chargeLevel ?? 0,
      skinId: (player as any).skinId ?? "",
      effectId: (player as any).effectId ?? "",
      tankId: (player as any).tankId ?? "",
      special: (player as any).special ?? "",
      specialPower: (player as any).specialPower ?? 1,
      stealthed: (player as any).stealthed ?? false,
      burningUntil: (player as any).burningUntil ?? 0,
    });

    (room.state.players as any).onAdd((player: PlayerState, key: string) => {
      console.log(`[useColyseus] Player added: ${key} (${player.name})`);
    });

    (room.state.players as any).onRemove((_player: PlayerState, key: string) => {
      console.log(`[useColyseus] Player removed: ${key}`);
      snapshotInterpolation.remove(key);
    });

    // Rastrear vida/morte para o evento playerIsAliveChanged
    const aliveTracker = new Map<string, boolean>();
    // Assinatura dos obstáculos para sincronizar destruição sem churn por patch
    let lastObstacleSig = "";

    // Estado do jogo atualizado
    room.onStateChange((state) => {
      // [NETCODE] Sincronizar relógio do servidor para a timeline de interpolação
      snapshotInterpolation.updateServerClock((state as any).serverTime || 0);
      const snapTime = (state as any).serverTime || Date.now();
      const localSid = get().sessionId;

      // Reconstruir o mapa de jogadores direto do estado (fonte de verdade)
      const playersMap = new Map<string, ColyseusPlayer>();
      state.players.forEach((player: PlayerState, key: string) => {
        playersMap.set(key, toColyseusPlayer(player));

        // Alimentar buffer de snapshots dos jogadores remotos
        if (key !== localSid && player.isAlive) {
          snapshotInterpolation.pushSnapshot(key, {
            t: snapTime,
            x: player.x,
            z: player.z,
            rotation: player.rotation,
            turretRotation: player.turretRotation,
          });
        }

        const wasAlive = aliveTracker.get(key);
        if (wasAlive === true && !player.isAlive) {
          window.dispatchEvent(new CustomEvent("playerIsAliveChanged", {
            detail: { playerId: key, isAlive: player.isAlive }
          }));
        }
        aliveTracker.set(key, player.isAlive);
      });
      for (const key of Array.from(aliveTracker.keys())) {
        if (!playersMap.has(key)) aliveTracker.delete(key);
      }


      // Converter bullets
      const bullets: ColyseusBullet[] = state.bullets.map((b: BulletState) => ({
        id: b.id,
        ownerId: b.ownerId,
        position: { x: b.x, y: b.y, z: b.z },
        direction: { x: b.dirX, y: b.dirY, z: b.dirZ },
        speed: b.speed,
        damage: b.damage,
        size: b.size,
        cosmeticEffectId: b.cosmeticEffectId || null,
        cosmeticEffectType: (b.cosmeticEffectType as CosmeticEffectType | undefined) || null,
      }));

      const items: ColyseusItem[] = (state.items || []).map((item: ItemState) => ({
        id: item.id,
        type: item.type,
        position: { x: item.x, y: item.y, z: item.z },
        createdAt: item.createdAt,
        expiresAt: item.expiresAt,
      }));

      // [NETCODE] Obstáculos server-authoritative → ObstacleData local.
      // Assinatura (id:health) evita reconstruir o array a cada patch;
      // muda apenas quando um obstáculo é danificado/destruído.
      const serverObstacles = (state as any).obstacles as { id: string; x: number; z: number; sizeX: number; sizeY: number; sizeZ: number; type: string; health: number; maxHealth: number }[] | undefined;
      let obstacleSig = "";
      if (serverObstacles && serverObstacles.length >= 0) {
        const parts: string[] = [];
        serverObstacles.forEach((o) => parts.push(`${o.id}:${o.health}`));
        obstacleSig = parts.join(",");
      }
      const convertObstacles = (): ObstacleData[] => {
        const result: ObstacleData[] = [];
        (serverObstacles || []).forEach((o) => {
          result.push({
            id: o.id,
            position: new THREE.Vector3(o.x, 0, o.z),
            size: new THREE.Vector3(o.sizeX, o.sizeY, o.sizeZ),
            type: o.type === "destructible" ? "destructible" : "indestructible",
            health: o.health,
            maxHealth: o.maxHealth,
          });
        });
        return result;
      };

      // Determinar game status
      let gameStatus: ColyseusState["gameStatus"] = "waiting";
      if (state.status === "countdown") gameStatus = "countdown";
      else if (state.status === "playing") gameStatus = "playing";
      else if (state.status === "paused") gameStatus = "paused";
      else if (state.status === "finished") gameStatus = "finished";
      
      // Calcular localPlayer e winner para compatibilidade
      const sessionId = get().sessionId;
      const localPlayerData = sessionId ? playersMap.get(sessionId) || null : null;
      const winnerData = (state.winnerId && state.winnerName) 
        ? { id: state.winnerId, name: state.winnerName } 
        : null;
      
      // Converter rules
      const rulesData: GameRulesState | null = state.rules ? {
        gameMode: state.rules.gameMode,
        killsToWin: state.rules.killsToWin,
        timeLimit: state.rules.timeLimit,
        livesPerPlayer: state.rules.livesPerPlayer,
        maxPlayers: state.rules.maxPlayers,
        allowSpectators: state.rules.allowSpectators,
        damageMultiplier: state.rules.damageMultiplier,
        speedMultiplier: state.rules.speedMultiplier,
        healthMultiplier: state.rules.healthMultiplier,
        tankSpecialsEnabled: (state.rules as any).tankSpecialsEnabled ?? true,
        enablePowerups: state.rules.enablePowerups,
        enabledItems: (state.rules as any).enabledItems ?? "",
        respawnTime: state.rules.respawnTime,
        respawnInvulnerability: state.rules.respawnInvulnerability,
        friendlyFire: state.rules.friendlyFire,
        // Bot settings (use defaults if not present)
        botCount: (state.rules as any).botCount ?? 0,
        botDifficulty: (state.rules as any).botDifficulty ?? 'normal',
        autoFillBots: (state.rules as any).autoFillBots ?? false,
        removeBotsOnJoin: (state.rules as any).removeBotsOnJoin ?? true,
        skillBasedBots: (state.rules as any).skillBasedBots ?? false,
      } : null;
      
      

      // [BR] Zona do Battle Royale: manter a MESMA referência entre patches
      // quando os parâmetros não mudaram (eles só mudam nas transições de
      // fase) — evita re-render dos assinantes a cada patch de estado.
      const stateBR = state as any;
      const prevZone = get().brZone;
      let brZone: ColyseusBRZone | null = prevZone;
      if (stateBR.brZoneActive) {
        if (
          !prevZone ||
          prevZone.fromSize !== stateBR.brZoneFromSize ||
          prevZone.toSize !== stateBR.brZoneToSize ||
          prevZone.shrinkStartAt !== stateBR.brZoneShrinkStartAt ||
          prevZone.shrinkEndAt !== stateBR.brZoneShrinkEndAt ||
          prevZone.nextShrinkAt !== stateBR.brNextShrinkAt ||
          prevZone.phase !== stateBR.brZonePhase
        ) {
          brZone = {
            fromSize: stateBR.brZoneFromSize,
            toSize: stateBR.brZoneToSize,
            shrinkStartAt: stateBR.brZoneShrinkStartAt,
            shrinkEndAt: stateBR.brZoneShrinkEndAt,
            nextShrinkAt: stateBR.brNextShrinkAt,
            phase: stateBR.brZonePhase,
          };
        }
      } else {
        brZone = null;
      }

      set({
        players: playersMap,
        bullets,
        items,
        gameStatus,
        countdownTime: state.countdownTime,
        gameSeed: state.gameSeed,
        terrain: state.terrain || null,
        roomCode: state.roomCode,
        isHost: state.hostId === sessionId,
        winnerId: state.winnerId || null,
        winnerName: state.winnerName || null,
        endReason: state.endReason || null,
        rules: rulesData,
        gameTimeRemaining: state.gameTimeRemaining || 0,
        brZone,
        localPlayer: localPlayerData,
        winner: winnerData,
      });

      // Sincronizar itens e power-ups para multiplayer
      const rocketanks = useRocketanks.getState();
      if (rocketanks.isMultiplayer) {
        // [NETCODE] Sincronizar obstáculos (destruição server-authoritative)
        if (gameInitialized && obstacleSig !== lastObstacleSig) {
          lastObstacleSig = obstacleSig;
          useRocketanks.setState({ obstacles: convertObstacles() });
        }

        rocketanks.syncMultiplayerItems(
          items.map((item) => ({
            id: item.id,
            type: item.type as any,
            position: new THREE.Vector3(item.position.x, item.position.y, item.position.z),
            createdAt: item.createdAt,
          }))
        );

        const localSessionId = get().sessionId;
        if (localSessionId) {
          const local = state.players.get(localSessionId);
          if (local) {
            rocketanks.syncMultiplayerPowerupState({
              heldItem: (local as any).heldItem || null,
              activeEffect: (local as any).activeEffect
                ? {
                    type: (local as any).activeEffect,
                    expiresAt: (local as any).effectExpiresAt || 0,
                    shieldHits: (local as any).shieldHits || 0,
                    decoyPosition:
                      (local as any).activeEffect === "decoyHologram" && (local as any).decoyExpiresAt > Date.now()
                        ? {
                            x: (local as any).decoyX || 0,
                            z: (local as any).decoyZ || 0,
                          }
                        : undefined,
                  }
                : null,
            });

            // [FIX] Sincronizar health/isAlive do servidor para o playerTank local.
            // O servidor é autoritativo para dano em multiplayer; sem este sync
            // o playerTank.health ficava desatualizado e o jogador nunca "morria".
            const localTank = rocketanks.playerTank;
            if (localTank) {
              const serverHealth = local.health;
              const serverIsAlive = local.isAlive;
              if (localTank.health !== serverHealth || localTank.isAlive !== serverIsAlive) {
                rocketanks.updatePlayerHealth(serverHealth);
              }
            }
          }
        }

        // [FIX] Sincronizar remotePlayersMap para o modo espectador funcionar.
        // Antes, remotePlayersMap nunca era populado, fazendo startMultiplayerSpectating()
        // sempre falhar (lista vazia) e o jogador morto ficava preso sem UI.
        const remoteMap = new Map<string, { id: string; sessionId: string; name: string; position: { x: number; y: number; z: number }; isAlive: boolean; health: number; color: string }>();
        playersMap.forEach((player, key) => {
          if (key !== localSessionId) {
            remoteMap.set(key, {
              id: player.id,
              sessionId: player.sessionId,
              name: player.name,
              position: player.position,
              isAlive: player.isAlive,
              health: player.health,
              color: player.color,
            });
          }
        });
        useRocketanks.setState({ remotePlayersMap: remoteMap });

        // [MP-SPECTATOR] Se o alvo espectado morreu, redireciona para o
        // próximo jogador vivo (antes ninguém chamava isto e o espectador
        // ficava preso num alvo morto).
        if (rocketanks.multiplayerIsSpectating) {
          rocketanks.updateMultiplayerSpectateTarget();
        }
      }
      
      // Inicializar jogo local quando começar (apenas uma vez!)
      if (state.status === "playing" && get().sessionId && !gameInitialized) {
        const localPlayer = state.players.get(get().sessionId!);
        if (localPlayer) {
          gameInitialized = true;
          lastObstacleSig = obstacleSig;
          useRocketanks.getState().initializeMultiplayerGame(
            { x: localPlayer.x, y: localPlayer.y, z: localPlayer.z },
            localPlayer.color,
            localPlayer.name,
            state.gameSeed,
            state.terrain, // Terrain escolhido pelo host
            state.arenaSize, // Tamanho da arena do servidor
            // [NETCODE] Obstáculos vêm do servidor (autoridade); o seed continua
            // como fallback para compatibilidade
            serverObstacles && serverObstacles.length > 0 ? convertObstacles() : undefined
          );
        }
      }
      
      // Resetar flag quando jogo termina ou volta ao lobby
      if (state.status === "finished" || state.status === "waiting") {
        gameInitialized = false;
      }


    });
    
    // Mensagens específicas
    room.onMessage("welcome", (data: { sessionId: string; roomCode: string; isHost: boolean }) => {
      console.log("[useColyseus] Welcome:", data);
      set({
        sessionId: data.sessionId,
        roomCode: data.roomCode,
        isHost: data.isHost,
      });
    });
    
    room.onMessage("error", (data: { message: string }) => {
      set({ error: data.message });
    });
    
    room.onMessage("hostChanged", (data: { newHostId: string }) => {
      set({ isHost: data.newHostId === get().sessionId });
    });
    
    // [FIX] Dedupe set para evitar processar mesmo kill 2x
    const seenKills = new Set<string>();
    
    room.onMessage("playerKilled", (data: {
      killId?: string;
      victimId: string;
      victimName: string;
      killerId: string;
      killerName: string;
      livesRemaining?: number;
      victimPosition?: { x: number; y: number; z: number };
      cosmeticEffectId?: string;
      cosmeticEffectType?: CosmeticEffectType;
    }) => {
      // Dedupe: se já processamos esse killId, ignorar
      if (data.killId) {
        if (seenKills.has(data.killId)) return;
        seenKills.add(data.killId);
        // [FIX-27] Limpar metade do set quando atingir 200 (antes: removia só 1)
        if (seenKills.size > 200) {
          const entries = Array.from(seenKills);
          for (let i = 0; i < 100; i++) {
            seenKills.delete(entries[i]);
          }
        }
      }
      // Emitir evento para UI de kill feed
      window.dispatchEvent(new CustomEvent("playerKilled", { detail: data }));
    });
    
    // [FIX] Ouvir evento de hit para feedback visual (impacto de tiro)
    room.onMessage("playerHit", (data: {
      victimId: string;
      victimName: string;
      shooterId: string;
      damage: number;
      remainingHealth: number;
      hitPosition: { x: number; y: number; z: number };
      cosmeticEffectId?: string;
      cosmeticEffectType?: CosmeticEffectType;
    }) => {
      window.dispatchEvent(new CustomEvent("playerHit", { detail: data }));
    });

    room.onMessage("shockwave", (data: { sourceId: string; sourceName: string; x: number; y: number; z: number }) => {
      window.dispatchEvent(new CustomEvent("shockwave", { detail: data }));
    });

    // [BESTIÁRIO] Fênix renasceu — VFX/som do renascimento em todos os clientes
    room.onMessage("specialRebirth", (data: {
      playerId: string;
      playerName: string;
      health: number;
      position: { x: number; y: number; z: number };
    }) => {
      window.dispatchEvent(new CustomEvent("specialRebirth", { detail: data }));
    });

    // [NETCODE] Tiro de jogador remoto — som/flash imediatos (sem esperar patch)
    room.onMessage("shotFired", (data: { ownerId: string; x: number; z: number; shotCount: number; chargeLevel: number }) => {
      if (data.ownerId === get().sessionId) return; // atirador já teve feedback local
      window.dispatchEvent(new CustomEvent("remoteShotFired", { detail: data }));
    });

    // [NETCODE] Bala destruída pelo servidor (obstáculo/parede) — VFX sincronizado
    room.onMessage("bulletExploded", (data: {
      bulletId: string;
      ownerId: string;
      x: number;
      y: number;
      z: number;
      obstacleId?: string;
      obstacleDestroyed?: boolean;
      cosmeticEffectId?: string;
      cosmeticEffectType?: string;
    }) => {
      window.dispatchEvent(new CustomEvent("bulletExploded", { detail: data }));
    });
    
    room.onMessage("playerAbandoned", (data: { playerId: string; playerName: string }) => {
      window.dispatchEvent(new CustomEvent("playerAbandoned", { detail: data }));
    });

    // [FIX] Listener para playerRespawned - garante sincronização após respawn
    room.onMessage("playerRespawned", (data: { playerId: string; playerName: string; newPosition: { x: number; y: number; z: number } }) => {
      console.log(`[useColyseus] Player respawned: ${data.playerName} at (${data.newPosition.x}, ${data.newPosition.z})`);
      window.dispatchEvent(new CustomEvent("playerRespawned", { detail: data }));
    });

    // [MULTIPLAYER-SPECTATOR] Listener para playerDefeated
    room.onMessage(
      "playerDefeated",
      (data: {
        playerId: string;
        playerName: string;
        defeatedBy: string;
        isBot: boolean;
      }) => {
        window.dispatchEvent(new CustomEvent("playerDefeated", { detail: data }));
      }
    );
    
    room.onMessage(
      "gamePaused",
      (data: {
        paused: boolean;
        pausedBy?: string;
        pausedByName?: string;
        resumedBy?: string;
        resumedByName?: string;
        playersSnapshot?: Array<{
          id: string;
          x: number;
          y: number;
          z: number;
          rotation: number;
          turretRotation: number;
          isAlive: boolean;
        }>;
        bulletsSnapshot?: Array<{
          id: string;
          ownerId: string;
          x: number;
          y: number;
          z: number;
          dirX: number;
          dirY: number;
          dirZ: number;
          speed: number;
          damage: number;
          size: number;
        }>;
      }) => {
        window.dispatchEvent(new CustomEvent("multiplayerPause", { detail: data }));
      }
    );
    
    room.onMessage("gameRestarted", (data: { terrain: string; gameSeed: number }) => {
      console.log("[useColyseus] Game restarted:", data);
      // Atualizar estado para voltar ao lobby da sala
      set({ gameStatus: "waiting", terrain: data.terrain || null, gameSeed: data.gameSeed || 0 });
      // Voltar o phase para "menu" para que o Menu/Lobby renderize

      useRocketanks.getState().setPhase("menu");
      // Disparar evento para que o jogo local também reinicie
      window.dispatchEvent(new CustomEvent("gameRestarted", { detail: data }));
    });
    
    room.onError((code, message) => {
      console.error("[useColyseus] Room error:", code, message);
      set({ error: message || `Error ${code}` });
    });
    
    room.onLeave((code) => {
      console.log("[useColyseus] Left room with code:", code);
      window.removeEventListener("skinSelected", skinChangedHandler);
      window.removeEventListener("tankSelected", tankChangedHandler);
      window.removeEventListener("effectSelected", effectChangedHandler);
      
      // Código 1000 = saída intencional, não tentar reconectar
      // Código 1006 = conexão perdida inesperadamente
      // Código 4000+ = erros de servidor/aplicação
      const wasIntentional = code === 1000;
      const wasGameFinished = get().gameStatus === "finished";
      
      snapshotInterpolation.clear();

      if (wasIntentional || wasGameFinished) {
        // Saída normal - limpar dados de reconexão e token
        clearReconnectData();
        clearReconnectToken();
        set({
          room: null,
          roomCode: null,
          isHost: false,
          players: new Map(),
          gameStatus: "lobby",
          terrain: null,
          canReconnect: false,
        });
      } else {
        // Desconexão inesperada - permitir reconexão
        console.log("[useColyseus] Unexpected disconnect, enabling reconnect...");
        set({
          room: null,
          isHost: false,
          players: new Map(),
          gameStatus: "lobby",
          terrain: null,
          error: "Conexão perdida. Tentando reconectar...",
          canReconnect: true,
        });
        
        // Tentar reconexão automática
        const state = get();
        if (state.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectTimeout = setTimeout(() => {
            get().tryReconnect();
          }, RECONNECT_DELAY_MS);
        }
      }
    });
  }
  
  return {
    // Estado inicial
    isConnected: false,
    room: null,
    sessionId: null,
    roomCode: null,
    isHost: false,
    players: new Map(),
    bullets: [],
    items: [],
    gameStatus: "lobby",
    countdownTime: 0,
    gameSeed: 0,
    terrain: null,
    winnerId: null,
    winnerName: null,
    endReason: null,
    rules: null,
    gameTimeRemaining: 0,
    brZone: null,
    localPlayer: null,
    winner: null,
    error: null,
    isReconnecting: false,
    reconnectAttempts: 0,
    canReconnect: false,

    connect: () => {
      try {
        getColyseusClient();
        set({ isConnected: true, error: null });
        console.log("[useColyseus] Connected");
      } catch (error) {
        console.error("[useColyseus] Connection error:", error);
        set({ error: "Falha ao conectar" });
      }
    },
    
    // [FIX-24] Tornar disconnect async para await room.leave()
    disconnect: async () => {
      const { room } = get();
      if (room) {
        try { await room.leave(); } catch (e) { console.warn('[useColyseus] leave error:', e); }
      }
      set({
        isConnected: false,
        room: null,
        sessionId: null,
        roomCode: null,
        players: new Map(),
        items: [],
        gameStatus: "lobby",
        terrain: null,
      });
    },
    
    createRoom: async (playerName: string, terrain?: string) => {
      try {
        set({ error: null, reconnectAttempts: 0, canReconnect: false });
        const skinId = useProgress.getState().selectedSkinId || undefined;
        const tankId = useProgress.getState().selectedTankId || undefined;
        const effectId = useProgress.getState().selectedEffectId || undefined;
        const room = await createRoom(playerName, terrain, undefined, skinId, tankId, effectId);
        setupRoomListeners(room, playerName);
        set({
          room,
          isConnected: true,
          gameStatus: "waiting",
        });
        return true;
      } catch (error: any) {
        console.error("[useColyseus] Create room error:", error);
        set({ error: translateColyseusError(error, "create") });
        return false;
      }
    },
    
    joinRoom: async (code: string, playerName: string) => {
      try {
        set({ error: null, reconnectAttempts: 0, canReconnect: false });
        const skinId = useProgress.getState().selectedSkinId || undefined;
        const tankId = useProgress.getState().selectedTankId || undefined;
        const effectId = useProgress.getState().selectedEffectId || undefined;
        const room = await joinRoom(code.toUpperCase(), playerName, skinId, tankId, effectId);
        setupRoomListeners(room, playerName);
        set({
          room,
          isConnected: true,
          gameStatus: "waiting",
        });
        return true;
      } catch (error: any) {
        console.error("[useColyseus] Join room error:", error);
        set({ error: translateColyseusError(error, "join") });
        return false;
      }
    },
    
    // [FIX-26] leaveRoom agora é async para await leaveColyseusRoom()
    leaveRoom: async () => {
      clearReconnectData();
      clearReconnectToken();
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
      await leaveColyseusRoom();
      snapshotInterpolation.clear();
      gameInitialized = false; // Reset flag ao sair da sala
      set({
        room: null,
        roomCode: null,
        isHost: false,
        players: new Map(),
        bullets: [],
        items: [],
        gameStatus: "lobby",
        terrain: null,
        winnerId: null,
        winnerName: null,
        endReason: null,
        rules: null,
        gameTimeRemaining: 0,
        brZone: null,
      });
    },

    startGame: () => {
      sendStartGame();
    },
    
    restartGame: () => {
      sendRestartGame();
    },
    
    togglePause: (paused: boolean) => {
      sendTogglePause(paused);
    },
    
    changeColor: (color: string) => {
      sendChangeColor(color);
      // Atualizar localmente para feedback imediato
      const { sessionId, players } = get();
      if (sessionId && players.has(sessionId)) {
        const player = players.get(sessionId)!;
        players.set(sessionId, { ...player, color });
        set({ players: new Map(players) });
      }
    },
    
    abandon: () => {
      sendAbandon();
    },
    
    toggleReady: () => {
      sendToggleReady();
    },
    
    updateRules: (rules: GameRulesOptions) => {
      const { isHost } = get();
      if (!isHost) {
        console.warn("[useColyseus] Only host can update rules");
        return;
      }
      sendUpdateRules(rules);
    },
    

    sendMove: (position, rotation, turretRotation) => {
      // IMPORTANTE: Verificar status de pause ANTES de enviar movimento
      // Previne desincronização em multiplayer
      if (get().gameStatus !== "playing") {
        // Não enviar movimento se jogo está pausado
        return;
      }

      const now = Date.now();
      if (now - lastMoveSentAt < MIN_MOVE_INTERVAL_MS) return;
      lastMoveSentAt = now;
      
      // Client-authoritative: envia posição direta para o servidor
      // Mais suave que tentar converter posição → input → posição
      sendMovePosition(position, rotation, turretRotation ?? 0);
    },
    
    sendHit: (targetId: string, damage: number) => {
      // Hit é calculado pelo servidor no Colyseus - não precisa enviar
      // Apenas log para debug
      console.log("[Colyseus] sendHit - server-authoritative, ignoring:", targetId, damage);
    },
    
    sendShoot: (bulletId: string, aimAngle: number) => {
      sendShoot(bulletId, aimAngle);
    },
    
    sendShootFull: (bulletId, position, direction, metadata) => {
      sendShootWithPosition(bulletId, position, direction, metadata);
    },

    sendShootVolley: (shots, chargeLevel, metadata) => {
      if (get().gameStatus !== "playing") return;
      sendShootVolley(shots, chargeLevel, metadata);
    },
    
    sendChargeUpdate: (isCharging: boolean, chargeLevel: number) => {
      if (get().gameStatus !== "playing") return;

      const now = Date.now();
      if (now - lastChargeSentAt < MIN_CHARGE_INTERVAL_MS) return;
      lastChargeSentAt = now;

      sendChargeUpdate(isCharging, chargeLevel);
    },

    sendUseItem: () => {
      if (get().gameStatus !== "playing") return;
      sendUseItem();
    },

    sendUpdateSkin: (skinId: string) => {
      sendUpdateSkin(skinId);
    },
    
    getLocalPlayer: () => {
      const { sessionId, players } = get();
      if (!sessionId) return null;
      return players.get(sessionId) || null;
    },
    
    clearError: () => {
      set({ error: null });
    },
    
    // Reconexão usando reconnectionToken nativo do Colyseus
    // Preserva o sessionId/assento do jogador no servidor
    tryReconnect: async () => {
      const state = get();
      
      if (state.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.log("[useColyseus] Max reconnect attempts reached");
        clearReconnectToken();
        clearReconnectData();
        set({ 
          canReconnect: false, 
          isReconnecting: false,
          error: "Não foi possível reconectar. Tente novamente.",
        });
        return false;
      }
      
      set({ 
        isReconnecting: true, 
        reconnectAttempts: state.reconnectAttempts + 1,
        error: `Reconectando... (tentativa ${state.reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`,
      });
      
      try {
        // 1) Tenta reconnect nativo do Colyseus (preserva assento)
        console.log("[useColyseus] Attempting native Colyseus reconnect...");
        const room = await reconnectRoom();
        
        if (room) {
          setupRoomListeners(room);
          set({
            room,
            isConnected: true,
            isReconnecting: false,
            reconnectAttempts: 0,
            canReconnect: false,
            error: null,
          });
          console.log("[useColyseus] Native reconnect successful!");
          return true;
        }
        
        // 2) Fallback: tenta join normal usando dados salvos
        const reconnectData = getReconnectData();
        if (reconnectData) {
          console.log("[useColyseus] Native reconnect failed, trying join fallback to:", reconnectData.roomCode);
          const fallbackRoom = await joinRoom(reconnectData.roomCode, reconnectData.playerName);
          setupRoomListeners(fallbackRoom, reconnectData.playerName);
          
          set({
            room: fallbackRoom,
            isConnected: true,
            gameStatus: "waiting",
            terrain: null,
            isReconnecting: false,
            reconnectAttempts: 0,
            canReconnect: false,
            error: null,
          });
          console.log("[useColyseus] Fallback reconnect successful!");
          return true;
        }
        
        throw new Error("No reconnection data available");
      } catch (error: any) {
        console.error("[useColyseus] Reconnect failed:", error);
        
        // Se a sala não existe mais, não tentar de novo
        if (error.message?.includes("not found") || error.message?.includes("does not exist")) {
          clearReconnectToken();
          clearReconnectData();
          set({
            isReconnecting: false,
            canReconnect: false,
            error: "Sala não existe mais",
          });
          return false;
        }
        
        // Tentar novamente depois de um delay
        if (state.reconnectAttempts + 1 < MAX_RECONNECT_ATTEMPTS) {
          reconnectTimeout = setTimeout(() => {
            get().tryReconnect();
          }, RECONNECT_DELAY_MS);
        } else {
          clearReconnectToken();
          clearReconnectData();
          set({
            isReconnecting: false,
            canReconnect: false,
            error: "Não foi possível reconectar",
          });
        }
        
        return false;
      }
    },
    
    cancelReconnect: () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
      clearReconnectData();
      clearReconnectToken();
      set({
        isReconnecting: false,
        reconnectAttempts: 0,
        canReconnect: false,
        error: null,
      });
    },
    
    checkReconnect: () => {
      const data = getReconnectData();
      if (data) {
        set({ canReconnect: true });
        return true;
      }
      return false;
    },
    
    reset: () => {
      clearReconnectData();
      clearReconnectToken();
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
      leaveColyseusRoom();
      snapshotInterpolation.clear();
      gameInitialized = false; // Reset flag ao resetar
      set({
        isConnected: false,
        room: null,
        sessionId: null,
        roomCode: null,
        isHost: false,
        players: new Map(),
        bullets: [],
        items: [],
        gameStatus: "lobby",
        countdownTime: 0,
        gameSeed: 0,
        terrain: null,
        winnerId: null,
        winnerName: null,
        endReason: null,
        rules: null,
        gameTimeRemaining: 0,
        brZone: null,
        error: null,
        isReconnecting: false,
        reconnectAttempts: 0,
        canReconnect: false,
      });
    },
  };
});

/**
 * Cores dos jogadores (espelhado do servidor)
 */
export const COLYSEUS_PLAYER_COLORS = [
  "#22d3ee", "#f472b6", "#a855f7", "#84cc16",
  "#f97316", "#ef4444", "#3b82f6", "#fbbf24"
];
